<template>
  <div class="h-100">
    <app-home />

    <ingestion-quota-alert
      v-if="ingestionQuotaBanner"
      :quota="ingestionQuotaBanner"
      prominent
      dense
      append-actions
      alert-class="ingestion-quota-banner mb-0"
      @compare="openQuotaPricing"
      @manage="openQuotaManageData"
    />

    <!-- Dialog for general messages -->
    <custom-dialog
      v-model="dialog.show"
      :title="dialog.title"
      :text="dialog.text"
      :action="dialog.action"
    />
    <claim-profile-dialog v-model="claimProfileDialog" />

    <!-- Websocket disconnection dialog -->
    <connection-lost-dialog
      v-model="wsDisconnected"
      :reconnecting="wsReconnecting"
      :attempts="wsReconnectAttempts"
      @reconnect="attemptReconnect"
    />

    <!-- Terms of Service Dialog -->
    <to-s-dialog
      :show="showTosDialog"
      :content="ToSContent"
      :is-update="isTosReacceptance"
      :current-version="$store.state.auth.currentTosVersion || ''"
      @accept="acceptTos"
      @decline="declineTos"
    />

    <!-- Subscription Dialog -->
    <v-dialog
      v-model="subscriptionDialog.show"
      max-width="1040"
      width="96vw"
      v-if="!isShareLink() && isBillingEnabled"
    >
      <v-card class="billing-shell-card" flat>
        <PurchaseSubscription
          :inquiry-only="subscriptionDialog.inquiryOnly"
          :open-enterprise-token="subscriptionDialog.openEnterpriseToken"
          :preset-deployment-type="subscriptionDialog.enterprisePresetDeployment"
          :selected-plan="subscriptionDialog.selectedPlan"
          :selected-interval="subscriptionDialog.selectedInterval"
          :source="subscriptionDialog.source"
          :campaign="subscriptionDialog.campaign"
          @enterprise-modal-visibility="onEnterpriseModalVisibility"
        />
      </v-card>
    </v-dialog>

    <!-- Google One Tap prompt on login and for demo sessions -->
    <google-one-tap
      v-if="showGoogleOneTap"
      :key="googleOneTapKey"
      :reprompt-ms="60000"
    />

    <!-- One-time welcome for newly registered users -->
    <welcome-dialog
      v-model="welcomeDialog"
      :user-name="welcomeUserName"
      :show-plans="isBillingEnabled && !isShareLink()"
      @explore-plans="openPricingModal({ source: 'welcome_dialog' })"
    />

    <!-- Subscription activation thank-you / plan change confirmation -->
    <plan-celebration-dialog
      v-model="planCelebration.show"
      :mode="planCelebration.mode"
      :plan-label="planCelebration.planLabel"
      :previous-plan-label="planCelebration.previousPlanLabel"
    />
  </div>
</template>

<script>
import CustomDialog from "@/components/common/Dialog.vue";
import ConnectionLostDialog from "@/components/common/ConnectionLostDialog.vue";
import ToSDialog from "@/components/common/ToSDialog.vue";
import ClaimProfileDialog from "@/components/common/ClaimProfileDialog.vue";
import IngestionQuotaAlert from "@/components/IngestionQuotaAlert.vue";
import { createRgbVarsForThemes } from "@/js/utils.vuetify.css";
import { getPredictors, getUserSettings } from "@/js/api.schema";
import { getIngestionQuotaAlertState } from "@/js/ingestionQuota";
import { isTosReacceptance, requiresTosAcceptance } from "@/js/utils.tos";
import { applyStudySummaryUserSettings } from "@/study-summary/settings";

import * as wsSingle from "@/js/api.websocket.single";
import {
  disconnect as disconnectSriesDownload,
  openSeriesWebsocket as openDownloadWs
} from "@/js/download/seriesDownload";
import AppHome from "@/views/Home";
import { mapGetters } from "vuex";
import Vue from "vue";
import PurchaseSubscription from "@/components/PurchaseSubscription.vue";
import GoogleOneTap from "@/plugins/auth/google/components/GoogleOneTap.vue";
import WelcomeDialog from "@/components/conversion/WelcomeDialog.vue";
import PlanCelebrationDialog from "@/components/conversion/PlanCelebrationDialog.vue";
import {
  consumeActivationCelebrated,
  getLastKnownTier,
  hasSeenWelcome,
  isRecentSignup,
  markActivationCelebrated,
  markWelcomeSeen,
  setLastKnownTier
} from "@/js/conversionPrompts";
import ToSString from "./ToS.js"; // if in same directory

export default {
  name: "App",
  components: {
    AppHome,
    ConnectionLostDialog,
    CustomDialog,
    GoogleOneTap,
    ClaimProfileDialog,
    ToSDialog,
    PurchaseSubscription,
    WelcomeDialog,
    PlanCelebrationDialog,
    IngestionQuotaAlert
  },
  data() {
    return {
      dialog: { show: false, title: "", text: "", action: null },
      wsDisconnected: false,
      wsReconnecting: false,
      wsReconnectAttempts: 0,
      subscriptionDialog: {
        show: false,
        openEnterpriseToken: 0,
        inquiryOnly: false,
        enterprisePresetDeployment: "",
        selectedPlan: "",
        selectedInterval: "",
        source: "",
        campaign: ""
      },
      subscriptionCheckInterval: null,
      pricingShownRoutes: new Set(),
      claimProfileDialog: false,
      welcomeDialog: false,
      planCelebration: {
        show: false,
        mode: "activated",
        planLabel: "",
        previousPlanLabel: ""
      }
    };
  },
  created() {
    // Connect to websocket, disconnect on page unload

    try {
      wsSingle.disconnect();
      disconnectSriesDownload();
    } catch (error) {
      // Handle the error here, you can log it or perform other actions.
      console.error("An error occurred:", error);
    }
  },
  mounted() {
    this.$root.$on("show-connection-dialog", () => {
      this.wsDisconnected = true;
    });
    this.$root.$on("open-claim-profile", this.openClaimProfileDialog);
    this.$root.$on("open-pricing-modal", this.openPricingModal);
    this.$root.$on("open-enterprise-inquiry", this.openEnterpriseInquiryModal);

    createRgbVarsForThemes(this.$vuetify.theme.themes.light);

    this.handleCheckoutQuery();
    this.openPricingFromRoute();

    // Slight delay to ensure all components are ready
    setTimeout(() => {
      if (this.isAuth) {
        this.$store.dispatch("billing/refreshBillingData");
        this.evaluateTierTransition();
        this.maybeShowWelcome();
      }

      // Set 5-minute interval
      this.subscriptionCheckInterval = setInterval(() => {
        if (this.isAuth) {
          this.$store.dispatch("billing/refreshBillingData");
        }
      }, 5 * 60 * 1000); // 5 minutes
    }, 500); // Adjust delay if needed
  },
  beforeDestroy() {
    if (this.subscriptionCheckInterval) {
      clearInterval(this.subscriptionCheckInterval);
      this.subscriptionCheckInterval = null;
    }
    this.$root.$off("open-claim-profile", this.openClaimProfileDialog);
    this.$root.$off("open-pricing-modal", this.openPricingModal);
    this.$root.$off("open-enterprise-inquiry", this.openEnterpriseInquiryModal);
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    ...mapGetters("billing", [
      "isBillingEnabled",
      "ingestionQuota",
      "ingestionQuotaExceeded"
    ]),
    ingestionQuotaBanner() {
      return getIngestionQuotaAlertState(
        this.ingestionQuota,
        this.ingestionQuotaExceeded
      );
    },
    requiresTosAcceptance() {
      return requiresTosAcceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    isTosReacceptance() {
      return isTosReacceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    showTosDialog() {
      const user = this.$store.state.auth.user;
      return (
        this.isAuth &&
        !this.isShareLink() &&
        Boolean(user) &&
        (this.requiresTosAcceptance || !user.accepted_tos)
      );
    },
    ToSContent() {
      return ToSString; // Ensure Vue tracks it
    },
    hasPaidSubscription() {
      return this.$store.state.auth.user?.subscription_tier !== "free";
    },
    showGoogleOneTap() {
      const isDemoUser = this.$store.state.auth.user?.is_demo === true;
      const isLoginRoute = this.$route?.name === "login";
      return isDemoUser || isLoginRoute;
    },
    googleOneTapKey() {
      const routeName = this.$route?.name || "no-route";
      const userId = this.$store.state.auth.user?.id || "anon";
      const isDemoUser = this.$store.state.auth.user?.is_demo ? "demo" : "std";
      return `${routeName}:${userId}:${isDemoUser}`;
    },
    welcomeUserName() {
      return this.$store.state.auth.user?.name || "";
    },
    userTier() {
      const user = this.$store.state.auth.user;
      if (!user) return null;
      return (user.subscription_tier || "free").toLowerCase();
    }
  },
  methods: {
    openQuotaManageData() {
      const target = this.ingestionQuotaBanner?.manageDataUrl || "/#/exams";
      this.openQuotaTarget(target);
    },
    openPricingFromRoute() {
      // Mirror openPricingModal's own guards here so a route that can never
      // actually open the modal (billing disabled, share-link session) also
      // never records a pricing_shown attribution event for it.
      if (
        this.$route?.name !== "pricing" ||
        !this.isAuth ||
        !this.isBillingEnabled ||
        this.isShareLink()
      ) {
        return;
      }
      const query = this.$route.query || {};
      this.subscriptionDialog.selectedPlan = ["base", "pro"].includes(query.plan)
        ? query.plan
        : "";
      this.subscriptionDialog.selectedInterval = [
        "monthly",
        "yearly"
      ].includes(query.interval)
        ? query.interval
        : "";
      this.subscriptionDialog.source = String(query.source || "");
      this.subscriptionDialog.campaign = String(query.campaign || "");
      this.openPricingModal({ preserveAttribution: true });

      const pricingRouteKey = this.$route.fullPath;
      this.recordPricingShown({ dedupeKey: pricingRouteKey });
    },
    recordPricingShown({ dedupeKey = "" } = {}) {
      if (dedupeKey) {
        if (this.pricingShownRoutes.has(dedupeKey)) return;
        this.pricingShownRoutes.add(dedupeKey);
      }
      this.$http
        .post("api/pricing-events/", {
          event_type: "pricing_shown",
          source: this.subscriptionDialog.source,
          campaign: this.subscriptionDialog.campaign,
          plan: this.subscriptionDialog.selectedPlan
        })
        .catch(() => {});
    },
    openClaimProfileDialog() {
      this.claimProfileDialog = true;
    },
    openPricingModal(
      { preserveAttribution = false, source = "", campaign = "" } = {}
    ) {
      if (!this.isBillingEnabled || this.isShareLink()) {
        return;
      }
      if (!preserveAttribution) {
        this.subscriptionDialog.selectedPlan = "";
        this.subscriptionDialog.selectedInterval = "";
        this.subscriptionDialog.source = source;
        this.subscriptionDialog.campaign = campaign;
      }
      this.subscriptionDialog.inquiryOnly = false;
      this.subscriptionDialog.enterprisePresetDeployment = "";
      this.subscriptionDialog.show = true;
      if (!preserveAttribution) {
        this.recordPricingShown();
      }
    },
    openQuotaPricing() {
      const target = this.ingestionQuotaBanner?.upgradeUrl;
      if (target) {
        this.openQuotaTarget(target);
        return;
      }
      this.openPricingModal({ source: "quota_banner" });
    },
    openQuotaTarget(target) {
      this.$router.push(target.replace(/^\/#/, "")).catch(() => {});
    },
    openEnterpriseInquiryModal() {
      if (!this.isBillingEnabled) {
        window.location.href = "mailto:info@dicom.vision";
        return;
      }
      this.subscriptionDialog.inquiryOnly = true;
      this.subscriptionDialog.enterprisePresetDeployment = "MDR";
      this.subscriptionDialog.show = true;
      this.subscriptionDialog.openEnterpriseToken += 1;
    },
    onEnterpriseModalVisibility(isVisible) {
      if (this.subscriptionDialog.inquiryOnly && isVisible) {
        this.subscriptionDialog.show = false;
      }
      if (!isVisible) {
        this.subscriptionDialog.inquiryOnly = false;
        this.subscriptionDialog.enterprisePresetDeployment = "";
      }
    },
    isShareLink() {
      return !!this.$route.params.shareId;
    },
    tierLabel(tierId) {
      if (!tierId || tierId === "free") return "Free";
      const catalogTier = (
        this.$store.getters["billing/catalog"]?.tiers || []
      ).find(tier => tier.id === tierId);
      if (catalogTier?.label) return catalogTier.label;
      const fallback = { base: "Basic", pro: "Pro", enterprise: "Enterprise" };
      return fallback[tierId] || tierId;
    },
    openPlanCelebration(mode, tierId, previousTierId) {
      this.planCelebration.mode = mode;
      this.planCelebration.planLabel = tierId ? this.tierLabel(tierId) : "";
      this.planCelebration.previousPlanLabel = previousTierId
        ? this.tierLabel(previousTierId)
        : "";
      this.planCelebration.show = true;
    },
    // Shows the thank-you modal when returning from a successful Stripe
    // checkout (BILLING_SUCCESS_URL points to /#/exams?checkout=success).
    handleCheckoutQuery() {
      if (this.$route?.query?.checkout !== "success") return;
      const user = this.$store.state.auth.user;
      // Session may not be validated yet on first call; keep the query
      // param so the isAuth / $route watchers can retry.
      if (!this.isAuth || !user) return;
      const query = { ...this.$route.query };
      delete query.checkout;
      this.$router.replace({ query }).catch(() => {});
      // Suppress the duplicate celebration the tier watcher would fire
      // once the webhook flips the tier to paid.
      markActivationCelebrated(user.id);
      const tier = this.userTier;
      if (tier && tier !== "free") {
        setLastKnownTier(user.id, tier);
      }
      this.openPlanCelebration(
        "activated",
        tier !== "free" ? tier : null,
        null
      );
    },
    // Detects subscription tier transitions (webhook sync, billing portal
    // changes) and celebrates activations / confirms plan changes.
    evaluateTierTransition() {
      const user = this.$store.state.auth.user;
      if (!this.isAuth || !user || user.is_demo || user.is_temp) return;
      const tier = this.userTier;
      if (!tier) return;
      const previous = getLastKnownTier(user.id);
      if (previous === tier) return;
      setLastKnownTier(user.id, tier);
      // First observation for this user in this browser: just record it.
      if (!previous) return;
      const isPaid = value => !!value && value !== "free";
      if (!isPaid(previous) && isPaid(tier)) {
        if (consumeActivationCelebrated(user.id)) return;
        this.openPlanCelebration("activated", tier, null);
      } else if (isPaid(previous)) {
        consumeActivationCelebrated(user.id); // clear any stale flag
        this.openPlanCelebration("changed", tier, previous);
      }
    },
    maybeShowWelcome() {
      const user = this.$store.state.auth.user;
      if (!this.isAuth || !user || user.is_demo || user.is_temp) return;
      if (this.isShareLink() || this.showTosDialog) return;
      if (this.welcomeDialog || this.planCelebration.show) return;
      if (!isRecentSignup(user) || hasSeenWelcome(user.id)) return;
      markWelcomeSeen(user.id);
      this.welcomeDialog = true;
    },
    isDownloadEnabled() {
      return true; //return !this.isAuth;
    },
    isUploadEnabled() {
      return true; // return this.isAuth && !this.$isDemoUser;
    },
    connectWebsocket() {
      this.wsDisconnected = false;
      this.wsReconnecting = false;

      const onClose = () => {
        if (this.isShareLink()) {
          return;
        }
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };
      const onError = () => {
        if (this.isShareLink()) {
          return;
        }
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };

      const userId =
        (this.$store.state.auth.user && this.$store.state.auth.user.uuid) ||
        this.user;
      if (this.isDownloadEnabled())
        openDownloadWs(userId, onClose, onError, this.$router);
    },
    attemptReconnect() {
      this.wsReconnecting = true;
      this.wsReconnectAttempts++;

      // Disconnect existing sockets before reconnecting
      try {
        wsSingle.disconnect();
        disconnectSriesDownload();
      } catch (e) {
        // ignore cleanup errors
      }

      // Small delay to let the old connections close cleanly
      setTimeout(() => {
        this.wsDisconnected = false;
        this.wsReconnecting = false;
        this.connectWebsocket();
      }, 1000);
    },
    async loadPredictors() {
      // get all predictors
      const predictors = await getPredictors();
      this.$store.commit("viewer/initPredictors", predictors);
    },
    loadUserSettings() {
      getUserSettings()
        .then(data => {
          // update store values
          this.$store.commit("viewer/initOptions", data);
          if (data.show_ground_truth_panel !== undefined) {
            this.$store.commit(
              "viewer/setShowGroundTruthPanel",
              data.show_ground_truth_panel
            );
          }

          // update dashboard series display settings
          if (data.hide_for_processing_series !== undefined) {
            this.$store.commit(
              "dashboard/setHideForProcessingSeries",
              data.hide_for_processing_series
            );
          }
          if (data.hide_non_imaging_series !== undefined) {
            this.$store.commit(
              "dashboard/setHideNonImagingSeries",
              data.hide_non_imaging_series
            );
          }
          if (data.group_proj_raw_series !== undefined) {
            this.$store.commit(
              "dashboard/setGroupProjRawSeries",
              data.group_proj_raw_series
            );
          }
          if (data.dashboard_triage_colors_enabled !== undefined) {
            this.$store.commit(
              "dashboard/setTriageColorsEnabled",
              data.dashboard_triage_colors_enabled
            );
          }
          applyStudySummaryUserSettings(this.$store, data);
          if (data.dark_mode !== undefined) {
            this.$store.commit("setDarkMode", data.dark_mode);
          }
          if (data.compact_navbar !== undefined) {
            this.$store.commit("setCompactNavbar", data.compact_navbar);
          }
          if (data.dashboard_group !== undefined) {
            this.$store.commit("dashboard/updateGroup", data.dashboard_group);
          }
          if (data.mask_sensitive_data !== undefined) {
            this.$store.commit(
              "dashboard/setMaskSensitiveData",
              data.mask_sensitive_data
            );
          }
          if (this.$store.state.auth.user?.is_demo === true) {
            // Shared demo account: always start from the demo column defaults,
            // ignoring whatever a previous visitor saved.
            this.$store.commit("dashboard/APPLY_DEMO_DEFAULT_COLUMN_PREFS");
          } else {
            const rawPrefs = data.dashboard_column_prefs;
            let parsedPrefs = rawPrefs;
            if (typeof rawPrefs === "string") {
              try {
                parsedPrefs = JSON.parse(rawPrefs);
              } catch (err) {
                parsedPrefs = {};
                if (process.env.NODE_ENV !== "production") {
                  console.warn("Invalid dashboard_column_prefs JSON", err);
                }
              }
            }
            if (parsedPrefs && typeof parsedPrefs === "object") {
              this.$store.commit("dashboard/SET_COLUMN_PREFS", parsedPrefs);
            }
          }
        })
        .catch(error => console.error(error));
    },
    async acceptTos() {
      try {
        const locale = (navigator.language || "en").slice(0, 10);
        const versionFromStore =
          this.$store.state.auth.currentTosVersion ||
          this.$store.state.auth.user?.tos_version ||
          "legacy";
        const response = await Vue.$http.patch("auth/users/accept-tos", null, {
          accepted_tos: true,
          tos_version: versionFromStore,
          locale,
          source: "web_dialog"
        });
        const payload = response.body || response.data || {};
        const acceptedVersion = payload.current_tos_version || versionFromStore;

        // Update the user object in Vuex
        this.$store.commit("auth/updateUser", {
          accepted_tos: true,
          tos_version: acceptedVersion,
          tos_locale: locale,
          tos_accept_source: "web_dialog",
          tos_accepted_at: new Date().toISOString()
        });
        this.$store.commit("auth/update", {
          key: "tosUpdateRequired",
          value: false
        });
        this.$store.commit("auth/update", {
          key: "currentTosVersion",
          value: acceptedVersion
        });
      } catch (error) {
        const payload = error.body || error.data || {};
        if (payload.current_tos_version) {
          this.$store.commit("auth/update", {
            key: "currentTosVersion",
            value: payload.current_tos_version
          });
        }
        console.error("Failed to update TOS acceptance status:", error);
      }
    },
    declineTos() {
      // Handle TOS decline (e.g., log out the user or show a warning)
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    }
  },

  watch: {
    $route() {
      if (this.isShareLink()) {
        this.wsDisconnected = false;
        this.wsReconnecting = false;
      }
      this.handleCheckoutQuery();
      this.openPricingFromRoute();
    },
    userTier() {
      this.evaluateTierTransition();
    },
    showTosDialog(show) {
      // Greet new users only once the ToS dialog is out of the way.
      if (!show) {
        this.$nextTick(() => this.maybeShowWelcome());
      }
    },
    showGoogleOneTap(show) {
      // When the One Tap condition becomes false (non-demo user logged in),
      // explicitly cancel any lingering browser-level Google prompt.
      if (!show && window.google && window.google.accounts) {
        window.google.accounts.id.cancel();
      }
    },
    isAuth(value) {
      this.$store.commit("viewer/initViewerSessionId");
      if (value) {
        this.loadPredictors();
        this.loadUserSettings();
        this.$store.dispatch("billing/refreshBillingData");
        this.connectWebsocket();
        this.handleCheckoutQuery();
        this.evaluateTierTransition();
        this.$nextTick(() => this.maybeShowWelcome());
      } else {
        this.$store.commit("billing/reset");
        this.subscriptionDialog.show = false;
      }
    },
    isBillingEnabled(value) {
      if (!value) {
        this.subscriptionDialog.show = false;
      } else {
        this.openPricingFromRoute();
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/utils.scss";
@import "@/styles/fonts.scss";
@import "@/styles/main.scss";

.billing-shell-card {
  background: #1e1e1e !important;
  border: 1px solid #3a3a3a;
  color: #f5f5f5 !important;
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
